import { StarsButton } from '@ps/web-shared-components';
import classNames from 'clsx';
import { orderBy, partition } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';
import { Prompt } from '@swf/first';

import {
  TXTWCL_My_address_isnXt_here,
  TXTWCL_Okay,
  TXTWCL_There_has_been_an_issue_with_y_ELL
} from './AddressResults.messages.json';
import { notFoundButton, scroll, view } from './AddressResults.scss';
import SearchResult from './SearchResult/SearchResult';

export interface AddressList {
  id: string;
  type: string;
  text: string;
  highlight: string;
  description: string;
}

export interface AddressDetails {
  address: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  language: string;
  street: string;
  buildingNumber: string;
}

export enum SearchDataType {
  Address = 'Address',
  BuildingNumber = 'BuildingNumber',
  Street = 'Street',
  Locality = 'Locality'
}

interface Props {
  searchQuery: string;
  onResponse: (arg: AddressDetails[]) => void;
  onDismiss: () => void;
  className?: string;
  selectedCountry?: string | null;
}

const searchThreshold = 3;

const AddressResults = ({
  searchQuery,
  onResponse,
  onDismiss,
  className,
  selectedCountry
}: Props) => {
  const [searchResults, setSearchResults] = useState<AddressList[]>([]);
  const [errorPromptVisible, setErrorPromptVisible] = useState(false);
  const country = useSelector(select.account.getCountry) as string;
  const request = useRequest();

  const captureAddress = useCallback(
    (payload: { container?: string; text?: string; countries?: string }) =>
      request({
        service: 'address.lookup.captureAddress',
        body: { ...payload }
      }),
    [request]
  );

  const sortResults = (results: AddressList[]) => {
    const [items, clusters] = partition(results, { type: SearchDataType.Address });
    return [...orderBy(clusters, 'text', 'asc'), ...orderBy(items, 'text', 'asc')];
  };

  const clusterFind = async (payload: { container: string }) =>
    captureAddress(payload).subscribe(({ body: items }) => setSearchResults(sortResults(items)));

  const getAddressDetails = useCallback(
    async (payload: { id: string }) =>
      request({
        service: 'address.lookup.retrieveAddress',
        body: { ...payload }
      }).subscribe(({ body }) => onResponse(body)),
    [request, onResponse]
  );

  useEffect(() => {
    if (!searchQuery || searchQuery.length < searchThreshold) {
      setSearchResults([]);
    } else if (searchQuery && searchQuery.length >= searchThreshold) {
      captureAddress({
        text: searchQuery,
        countries: selectedCountry ? selectedCountry : country
      }).subscribe(
        ({ body: items }) => setSearchResults(sortResults(items)),
        () => {
          setErrorPromptVisible(true);
        }
      );
    }
  }, [searchQuery, captureAddress, country, selectedCountry]);

  return (
    <>
      {errorPromptVisible && (
        <Prompt
          title={{
            translation: TXTWCL_There_has_been_an_issue_with_y_ELL
          }}
          primaryButton={{ translation: TXTWCL_Okay }}
          variant="error"
          onClose={() => setErrorPromptVisible(false)}
        />
      )}
      {searchQuery.length >= searchThreshold && (
        <StarsButton
          variant="text"
          onClick={() => {
            onDismiss();
            setSearchResults([]);
          }}
          translation={TXTWCL_My_address_isnXt_here}
          className={notFoundButton}
        />
      )}
      {!!searchResults.length && (
        <div className={classNames(view, className)}>
          <div className={scroll}>
            <SearchResult
              searchResults={searchResults}
              clusterFind={clusterFind}
              getAddressDetails={getAddressDetails}
            />
          </div>
        </div>
      )}
    </>
  );
};
export default AddressResults;
